import React from "react";
import { Star, Send, Loader2 } from "lucide-react";
import { submitRating, getRatingSummary } from "../lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { toast } from "sonner";
import { AVATAR_HERO } from "../lib/constants";

const RATED_KEY = "shabba_rated_v1";

export const hasRated = () => localStorage.getItem(RATED_KEY) === "true";
const markRated = () => localStorage.setItem(RATED_KEY, "true");

const StarPicker = ({ value, onChange, size = 32 }) => {
  const [hover, setHover] = React.useState(0);
  return (
    <div className="flex items-center gap-1.5" data-testid="star-picker">
      {[1, 2, 3, 4, 5].map((n) => {
        const active = (hover || value) >= n;
        return (
          <button
            type="button"
            key={n}
            onMouseEnter={() => setHover(n)}
            onMouseLeave={() => setHover(0)}
            onClick={() => onChange(n)}
            className="transition-transform hover:scale-110"
            aria-label={`${n} star${n > 1 ? "s" : ""}`}
            data-testid={`star-${n}`}
          >
            <Star
              size={size}
              className={active ? "text-ochre" : "text-ink/20"}
              fill={active ? "currentColor" : "none"}
              strokeWidth={1.8}
            />
          </button>
        );
      })}
    </div>
  );
};

const useRatingForm = (onDone) => {
  const [stars, setStars] = React.useState(0);
  const [comment, setComment] = React.useState("");
  const [saving, setSaving] = React.useState(false);

  const submit = async () => {
    if (stars < 1) {
      toast.error("Please tap a star to rate first.");
      return;
    }
    setSaving(true);
    try {
      await submitRating(stars, comment.trim());
      markRated();
      toast.success("Thank you for rating Shabba Robo! 🙏");
      onDone && onDone();
    } catch {
      toast.error("Could not save your rating. Try again shortly.");
    } finally {
      setSaving(false);
    }
  };

  return { stars, setStars, comment, setComment, saving, submit };
};

const SummaryStars = ({ average }) => (
  <div className="flex items-center gap-1" data-testid="rating-summary-stars">
    {[1, 2, 3, 4, 5].map((n) => (
      <Star
        key={n}
        size={18}
        className="text-ochre"
        fill={average >= n - 0.25 ? "currentColor" : "none"}
        strokeWidth={1.8}
      />
    ))}
  </div>
);

// Permanent rating section on the page
export const RatingSection = () => {
  const [summary, setSummary] = React.useState({ average: 0, count: 0 });
  const [done, setDone] = React.useState(hasRated());
  const refresh = React.useCallback(() => {
    getRatingSummary().then(setSummary).catch(() => {});
  }, []);
  React.useEffect(() => {
    refresh();
  }, [refresh]);

  const form = useRatingForm(() => {
    setDone(true);
    refresh();
  });

  return (
    <section id="rate" className="max-w-7xl mx-auto px-5 sm:px-8 py-16 lg:py-24 scroll-mt-20" data-testid="rating-section">
      <div className="rounded-3xl bg-sand-light border border-border p-6 sm:p-10 grid lg:grid-cols-[1fr_1.1fr] gap-8 items-center">
        <div>
          <span className="text-sm uppercase tracking-[0.2em] font-bold text-clay">Rate Shabba Robo</span>
          <h2 className="font-display font-black text-ink text-3xl sm:text-4xl mt-3 leading-tight">
            How's your experience?
          </h2>
          <p className="text-ink/70 mt-4 leading-relaxed">
            Your rating helps Shabba Robo get better for everyone. Tap the stars and drop a word if you like.
          </p>
          {summary.count > 0 && (
            <div className="flex items-center gap-3 mt-5" data-testid="rating-summary">
              <SummaryStars average={summary.average} />
              <span className="text-ink/70 text-sm">
                <b className="text-ink">{summary.average}</b> / 5 · {summary.count} rating{summary.count > 1 ? "s" : ""}
              </span>
            </div>
          )}
        </div>

        <div className="bg-sand rounded-2xl border border-border p-6">
          {done ? (
            <div className="text-center py-6" data-testid="rating-thanks">
              <div className="w-14 h-14 mx-auto rounded-full bg-ochre/15 flex items-center justify-center">
                <Star size={26} className="text-ochre" fill="currentColor" />
              </div>
              <p className="font-display font-bold text-xl text-ink mt-4">You've rated us — thank you! 🙏</p>
              <p className="text-ink/60 text-sm mt-1">We appreciate you being part of the Shabba Robo family.</p>
            </div>
          ) : (
            <>
              <p className="text-sm font-semibold text-ink/70">Your rating</p>
              <div className="mt-3">
                <StarPicker value={form.stars} onChange={form.setStars} />
              </div>
              <textarea
                value={form.comment}
                onChange={(e) => form.setComment(e.target.value)}
                rows={3}
                maxLength={500}
                placeholder="Anything you'd like to add? (optional)"
                className="mt-4 w-full rounded-xl bg-sand-light border border-border px-4 py-3 outline-none focus:ring-2 focus:ring-clay/30 focus:border-clay transition-colors resize-none"
                data-testid="rating-comment-input"
              />
              <button
                onClick={form.submit}
                disabled={form.saving}
                className="mt-4 w-full rounded-full bg-clay text-white font-bold px-6 py-3.5 hover:bg-clay-hover transition-transform hover:-translate-y-0.5 disabled:opacity-60 flex items-center justify-center gap-2"
                data-testid="rating-submit-btn"
              >
                {form.saving ? <><Loader2 size={18} className="animate-spin" /> Saving...</> : <><Send size={18} /> Submit rating</>}
              </button>
            </>
          )}
        </div>
      </div>
    </section>
  );
};

// Popup shown once after a visitor's first answer
export const RatingDialog = ({ open, onOpenChange }) => {
  const form = useRatingForm(() => onOpenChange(false));
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="rating-dialog">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <img src={AVATAR_HERO} alt="Shabba Robo" className="w-11 h-11 rounded-full object-cover ring-2 ring-clay/30" />
            <div>
              <DialogTitle className="font-display">Enjoying Shabba Robo?</DialogTitle>
              <DialogDescription>Tap a star to rate your experience.</DialogDescription>
            </div>
          </div>
        </DialogHeader>
        <div className="pt-2">
          <div className="flex justify-center">
            <StarPicker value={form.stars} onChange={form.setStars} size={36} />
          </div>
          <textarea
            value={form.comment}
            onChange={(e) => form.setComment(e.target.value)}
            rows={3}
            maxLength={500}
            placeholder="Anything you'd like to add? (optional)"
            className="mt-4 w-full rounded-xl bg-sand border border-border px-4 py-3 outline-none focus:ring-2 focus:ring-clay/30 focus:border-clay transition-colors resize-none"
            data-testid="rating-dialog-comment"
          />
          <div className="flex gap-3 mt-4">
            <button
              onClick={() => onOpenChange(false)}
              className="flex-1 rounded-full border border-border text-ink/70 font-semibold px-4 py-3 hover:bg-sand transition-colors"
              data-testid="rating-dialog-later"
            >
              Maybe later
            </button>
            <button
              onClick={form.submit}
              disabled={form.saving}
              className="flex-1 rounded-full bg-clay text-white font-bold px-4 py-3 hover:bg-clay-hover transition-transform hover:-translate-y-0.5 disabled:opacity-60 flex items-center justify-center gap-2"
              data-testid="rating-dialog-submit"
            >
              {form.saving ? <Loader2 size={18} className="animate-spin" /> : "Submit"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
